import store from './store'
import access from './lib/access.js'
import constants from './constants'
import config from './config'

import SignupView from '@/containers/Access/SignupView.vue'
import AccessView from '@/containers/Access/Access.vue'
import GoogleRedirectView from '@/containers/Access/GoogleRedirectView.vue'
import MicrosoftRedirectView from '@/containers/Access/MicrosoftRedirectView.vue'
import SAMLRedirectView from '@/containers/Access/SAMLRedirectView/SAMLRedirectView.vue'
import LogoutView from '@/containers/Access/Logout.vue'
import AccessResetPassword from '@/containers/Access/AccessResetPassword.vue'
import OAuthCallbackView from '@/containers/OAuth/OAuthCallbackView.vue'
import ConsentView from '@/containers/OAuth/ConsentView.vue'
import Download from './containers/DownloadView.vue'
import Help from './components/HelpView.vue'
import Register from './containers/Access/Register.vue'
import DowntimeMigrationView from '@/App/DowntimeMigrationView.vue'

const layouts = constants.layouts

const routes = [
  {
    name: 'boards',
    path: '/boards',
    component: () =>
      import(
        /* webpackChunkName: "BoardsView" */ '@/containers/BoardsView/BoardsView.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    meta: {
      titleKey: 'boards.board_other',
    },
  },
  {
    name: 'createBoard',
    path: '/boards/create',
    component: () =>
      import(
        /* webpackChunkName: "CreateBoardView" */ '@/containers/CreateBoardView/CreateBoardView.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    meta: {
      titleKey: 'boards.create_board',
    },
  },
  {
    name: 'board',
    path: '/boards/:boardId',
    component: () =>
      import(
        /* webpackChunkName: "BoardView" */ '@/containers/BoardView/BoardView.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    meta: {
      titleKey: 'boards.board_one',
    },
  },
  {
    name: 'editBoard',
    path: '/boards/:boardId/edit',
    component: () =>
      import(
        /* webpackChunkName: "EditBoardView" */ '@/containers/EditBoardView/EditBoardView.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    meta: {
      titleKey: 'boards.edit_board',
    },
  },
  {
    name: 'downtime',
    path: '/downtime',
    component: DowntimeMigrationView,
    meta: {
      layout: layouts?.blank,
    },
  },
  {
    path: '/',
    redirect: '/home',
  },
  {
    path: '/home',
    name: 'home',
    component: () =>
      import(
        /* webpackChunkName: "HomeView" */ '@/containers/HomeView/HomeView.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    meta: {
      titleKey: 'common.home',
    },
  },
  {
    path: '/explore',
    name: 'explore',
    component: () =>
      import(
        /* webpackChunkName: "ExploreView" */ '@/containers/Explore/ExploreView.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    meta: {
      titleKey: 'explore.explore',
    },
  },
  {
    name: 'download',
    path: '/download',
    component: Download,
    meta: {
      layout: layouts?.blank,
    },
  },
  {
    name: 'error404',
    path: '/404',
    props: true,
    component: () =>
      import(/* webpackChunkName: "404View" */ '@/components/404View.vue'),
  },
  {
    name: 'NetworkError',
    path: '/network-error',
    component: () =>
      import(
        /* webpackChunkName: "NetworkErrorView" */ '@/containers/NetworkErrorView.vue'
      ),
  },
  {
    name: 'organizations',
    path: '/admin/organizations',
    component: () =>
      import(
        /* webpackChunkName: "Organizations" */ '@/components/Admin/Organizations/Organizations.vue'
      ),
    beforeEnter: validateRoute(requireAdminAuth),
    meta: {
      titleKey: 'common.organization_other',
    },
  },
  {
    name: 'organizationCreateView',
    path: '/admin/organizations/create',
    component: () =>
      import(
        /* webpackChunkName: "OrganizationCreateView" */ '@/containers/Admin/OrganizationsView/OrganizationCreateView.vue'
      ),
    beforeEnter: validateRoute(requireAdminAuth),
    meta: {
      titleKey: 'organization.create_organization',
    },
  },
  {
    name: 'organizationUsers',
    path: '/admin/organizations/:organizationId/users',
    component: () =>
      import(
        /* webpackChunkName: "OrganizationUsers" */ '@/components/Admin/Organizations/OrganizationUsers.vue'
      ),
    beforeEnter: validateRoute(requireAdminAuth),
    meta: {
      titleKey: 'menu.admin.admin_people',
    },
  },
  {
    name: 'organizationRevs',
    path: '/admin/organizations/:organizationId/revs',
    component: () =>
      import(
        /* webpackChunkName: "OrganizationRevs" */ '@/components/Admin/Organizations/OrganizationRevs.vue'
      ),
    beforeEnter: validateRoute(requireAdminAuth),
    meta: {
      titleKey: 'common.rev_other',
    },
  },
  {
    name: 'organizationChallenges',
    path: '/admin/organizations/:organizationId/challenges',
    component: () =>
      import(
        /* webpackChunkName: "OrganizationChallenges" */ '@/components/Admin/Organizations/OrganizationChallenges.vue'
      ),
    beforeEnter: validateRoute(requireAdminAuth),
    meta: {
      titleKey: 'common.challenge_other',
    },
  },

  {
    name: 'challenge',
    path: '/challenge/:organizationId/:challengeId',
    component: () =>
      import(
        /* webpackChunkName: "ChallengeView" */ '@/components/Challenges/ChallengeView.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    meta: {
      titleKey: 'common.challenge_one',
    },
  },
  {
    name: 'site-settings',
    path: '/organizations/:organizationId/sites/:siteId/settings',
    component: () =>
      import(
        /* webpackChunkName: "SiteSettingsView" */ '@/containers/SiteSettingsView/SiteSettingsView.vue'
      ),
    props: true,
    beforeEnter: validateRoute(requireAuth),
    meta: {
      titleKey: 'menu.site_settings',
    },
  },
  {
    name: 'organization-settings',
    path: '/organization-settings/:organizationId',
    component: () =>
      import(
        /* webpackChunkName: "OrganizationSettingsView" */ '@/containers/OrganizationSettingsView/OrganizationSettingsView.vue'
      ),
    props: true,
    beforeEnter: validateRoute(requireAuth),
    meta: {
      titleKey: 'menu.organization_settings',
    },
  },

  {
    name: 'access',
    path: '/access',
    component: AccessView,
    meta: {
      layout: layouts?.blank,
      titleKey: 'auth.access.login',
    },
  },
  {
    name: 'GoogleRedirectView',
    path: '/access/google',
    component: GoogleRedirectView,
    meta: {
      layout: layouts?.blank,
    },
  },
  {
    name: 'MicrosoftRedirectView',
    path: '/access/microsoft',
    component: MicrosoftRedirectView,
    meta: {
      layout: layouts?.blank,
    },
  },
  {
    name: 'SAMLRedirectView',
    path: '/access/saml',
    component: SAMLRedirectView,
    meta: {
      layout: layouts?.blank,
    },
  },
  {
    name: 'OAuthAuthorize',
    path: '/oauth/authorize',
    component: OAuthCallbackView,
    meta: {
      layout: layouts?.blank,
    },
  },
  {
    name: 'OAuthConsent',
    path: '/oauth/consent',
    component: ConsentView,
    meta: {
      layout: layouts?.blank,
    },
  },
  {
    name: 'register',
    path: '/register',
    component: Register,
    meta: {
      layout: layouts?.blank,
    },
  },
  {
    name: 'resetPassword',
    path: '/reset-password/:token',
    component: AccessResetPassword,
    meta: {
      layout: layouts?.blank,
    },
  },
  {
    name: 'signUp',
    path: '/signup',
    component: SignupView,
    beforeEnter: requireNotAuth,
    meta: {
      layout: layouts?.blank,
    },
  },
  {
    name: 'logout',
    path: '/logout',
    component: LogoutView,
    beforeEnter: requireAuth,
    meta: {
      layout: layouts?.blank,
    },
  },
  {
    name: 'people',
    path: '/people',
    component: () =>
      import(/* webpackChunkName: "People" */ '@/components/People/People.vue'),
    beforeEnter: validateRoute(requireAuth),
    meta: {
      isProtected: true,
      acl: 'people',
      titleKey: 'people.people',
    },
  },
  {
    name: 'addUsersBulk',
    path: '/add-users-bulk',
    component: () =>
      import(
        /* webpackChunkName: "AddUsersBulkView" */ '@/containers/AddUsersBulk/AddUsersBulkView.vue'
      ),
    props: true,
    beforeEnter: validateRoute(requireLeadershipOrCoach),
    meta: {
      titleKey: 'common.users.add_users_bulk_title',
    },
  },
  {
    name: 'profile',
    path: '/profile/:organizationId/:userId',
    component: () =>
      import(
        /* webpackChunkName: "ProfileView" */ '@/containers/ProfileView/ProfileView.vue'
      ),
    props: true,
    beforeEnter: validateRoute(requireAuth),
    meta: {
      titleKey: 'common.profile',
    },
  },
  {
    name: 'teams',
    path: '/teams',
    component: () =>
      import(
        /* webpackChunkName: "TeamsView" */ '@/containers/TeamsView/TeamsView.vue'
      ),
    props: true,
    beforeEnter: validateRoute(requireAuth),
    meta: {
      titleKey: 'common.team_other',
    },
  },
  {
    name: 'teamDetails',
    path: '/teams/:teamId',
    component: () =>
      import(
        /* webpackChunkName: "TeamDetailsView" */ '@/containers/TeamDetailsView/TeamDetailsView.vue'
      ),
    props: true,
    beforeEnter: validateRoute(requireAuth),
    meta: {
      titleKey: 'common.team_one',
    },
  },
  {
    name: 'improvementIdea',
    path: '/rev/:organizationId/:revId',
    component: () =>
      import(
        /* webpackChunkName: "ImprovementIdeaView" */ '@/containers/ImprovementIdeaView/ImprovementIdeaView.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    props: true,
    meta: {
      titleKey: 'common.improvement_idea_one',
    },
  },
  {
    name: 'problemSolving',
    path: '/problem-solving/:organizationId/:revId',
    component: () =>
      import(
        /* webpackChunkName: "ProblemSolvingView" */ '@/containers/ProblemSolvingView/ProblemSolvingView.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    props: true,
    meta: {
      titleKey: 'common.problem_solving_one',
    },
  },
  {
    name: 'quickAction',
    path: '/quick-action/:organizationId/:revId',
    component: () =>
      import(
        /* webpackChunkName: "QuickActionView" */ '@/containers/QuickActionView/QuickActionView.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    props: true,
    meta: {
      titleKey: 'common.quick_action_one',
    },
  },
  {
    name: 'improvementIdeaPrint',
    path: '/rev/:organizationId/:revId/print',
    component: () =>
      import(
        /* webpackChunkName: "ImprovementIdeaPrintView" */ '@/containers/ImprovementIdeaPrintView/ImprovementIdeaPrintView.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    props: true,
    meta: {
      layout: layouts?.blank,
      titleKey: 'common.improvement_idea_one',
    },
  },
  {
    name: 'problemSolvingPrint',
    path: '/problem-solving/:organizationId/:revId/print',
    component: () =>
      import(
        /* webpackChunkName: "ProblemSolvingPrintView" */ '@/containers/ProblemSolvingPrintView/ProblemSolvingPrintView.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    props: true,
    meta: {
      layout: layouts?.blank,
      titleKey: 'common.problem_solving_one',
    },
  },
  {
    name: 'quickActionPrint',
    path: '/quick-action/:organizationId/:revId/print',
    component: () =>
      import(
        /* webpackChunkName: "QuickActionPrintView" */ '@/containers/QuickActionPrintView/QuickActionPrintView.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    props: true,
    meta: {
      layout: layouts?.blank,
      titleKey: 'common.quick_action_one',
    },
  },
  {
    name: 'validations',
    path: '/validations',
    component: () =>
      import(
        /* webpackChunkName: "ValidationsView" */ '@/containers/ValidationsView/ValidationsView.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    meta: {
      isProtected: true,
      acl: 'validations',
      titleKey: 'rev.validation.validation_other',
    },
  },
  {
    name: 'profileSettings',
    path: '/profile-settings',
    component: () =>
      import(
        /* webpackChunkName: "ProfileSettingsView" */ '@/containers/ProfileSettingsView/ProfileSettingsView.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    meta: {
      titleKey: 'profile_settings.edit_profile',
    },
  },
  {
    name: 'help',
    path: '/help',
    component: Help,
  },
  {
    name: 'insights',
    path: '/insights',
    component: () =>
      import(
        /* webpackChunkName: "ReportsView" */ '@/containers/ReportsView/ReportsView.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    meta: {
      isProtected: true,
      acl: 'insights',
      titleKey: 'insights.insights',
    },
  },
  {
    name: 'reports',
    path: '/insights/reports',
    component: () =>
      import(
        /* webpackChunkName: "ReportsView" */ '@/containers/ReportsView/ReportsView.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    meta: {
      isProtected: true,
      acl: 'reports',
      titleKey: 'insights.report_other',
    },
  },
  {
    name: 'insightsDashboard',
    path: '/insights/dashboard',
    component: () =>
      import(
        /* webpackChunkName: "InsightsView" */ '@/containers/InsightsView/InsightsView.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    meta: {
      isProtected: true,
      acl: 'insights',
      titleKey: 'insights.dashboard',
    },
  },
  {
    name: 'insightsPrint',
    path: '/insights/print',
    component: () =>
      import(
        /* webpackChunkName: "ReportsPrint" */ '@/containers/ReportsView/ReportsPrint.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    props: true,
    meta: {
      isProtected: true,
      acl: 'insights',
    },
  },
  {
    name: 'notifications',
    path: '/notifications',
    component: () =>
      import(
        /* webpackChunkName: "NotificationsPage" */ '@/components/Notifications/NotificationsPage.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    meta: {
      titleKey: 'notifications.notification_other',
    },
  },
  {
    name: 'adminAuditLogs',
    path: '/admin/logs',
    component: () =>
      import(
        /* webpackChunkName: "AdminAuditLogs" */ '@/components/AuditLogs/AdminAuditLogs.vue'
      ),
    beforeEnter: validateRoute(requireAdminAuth),
    meta: {
      titleKey: 'menu.admin.activity_logs',
    },
  },
  {
    name: 'organizationAuditLogs',
    path: '/settings/organization-activity',
    component: () =>
      import(
        /* webpackChunkName: "OrganizationAuditLogs" */ '@/components/AuditLogs/OrganizationAuditLogs.vue'
      ),
    beforeEnter: validateRoute(requireOwnerAuth),
    meta: {
      isProtected: true,
      acl: 'audit-logs',
      titleKey: 'menu.admin.organization_logs',
    },
  },
  {
    name: 'accountAuditLogs',
    path: '/settings/my-activity',
    component: () =>
      import(
        /* webpackChunkName: "AccountAuditLogs" */ '@/components/AuditLogs/AccountAuditLogs.vue'
      ),

    beforeEnter: validateRoute(requireAuth),
    meta: {
      isProtected: true,
      acl: 'audit-logs',
      titleKey: 'menu.user.my_activity',
    },
  },
  {
    name: 'complete-information',
    path: '/complete-information',
    component: () =>
      import(
        /* webpackChunkName: "CompleteLocationView" */ '@/containers/CompleteLocationView/CompleteLocationView.vue'
      ),
    beforeEnter: validateCompleteInformation(),
    meta: {
      layout: layouts?.blank,
    },
  },
  {
    name: 'accept-tocs',
    path: '/accept-tocs',
    component: () =>
      import(
        /* webpackChunkName: "AcceptTOCs" */ '@/containers/AcceptTOCs/AcceptTOCs'
      ),
    meta: {
      layout: layouts?.blank,
    },
  },

  {
    name: 'routineDefinitions',
    path: '/routine-definitions',
    component: () =>
      import(
        /* webpackChunkName: "RoutineDefinitionsView" */ '@/containers/RoutineDefinitionsView/RoutineDefinitionsView.vue'
      ),
    beforeEnter: validateRoute(requireRoutinesAuth),
    meta: {
      titleKey: 'routines.routine_other',
    },
  },
  {
    name: 'createRoutineDefinition',
    path: '/routine-definitions/create',
    component: () =>
      import(
        /* webpackChunkName: "RoutineDefinitionFormView" */ '@/containers/RoutineDefinitionFormView/RoutineDefinitionFormView.vue'
      ),
    beforeEnter: validateRoute(requireRoutinesAuth),
    meta: {
      titleKey: 'routines.new_routine',
    },
  },
  {
    name: 'routineDetail',
    path: '/routine-definitions/:routineId',
    component: () =>
      import(
        /* webpackChunkName: "RoutineDefinitionView" */ '@/containers/RoutineDefinitionView/RoutineDefinitionView.vue'
      ),
    beforeEnter: validateRoute(requireRoutinesAuth),
    meta: {
      titleKey: 'routines.routine_one',
    },
  },
  {
    name: 'routineDefinitionView',
    path: '/routine-definition/:routineId',
    component: () =>
      import(
        /* webpackChunkName: "RoutineDefinitionView" */ '@/containers/RoutineDefinitionView/RoutineDefinitionView.vue'
      ),
    beforeEnter: validateRoute(requireRoutinesAuth),
    meta: {
      titleKey: 'routines.routine_one',
    },
  },
  // This variant is used by Rever Admins to load info from a specific organization
  {
    name: 'reverAdminRoutineDefinitionView',
    path: '/routine-definition/:organizationId/:routineId',
    component: () =>
      import(
        /* webpackChunkName: "RoutineDefinitionView" */ '@/containers/RoutineDefinitionView/RoutineDefinitionView.vue'
      ),
    beforeEnter: validateRoute(requireRoutinesAuth),
    meta: {
      titleKey: 'routines.routine_one',
    },
  },
  {
    name: 'routinePrintView',
    path: '/routine-definitions/:routineExecutionId/print',
    component: () =>
      import(
        /* webpackChunkName: "RoutinePrintView" */ '@/containers/RoutineExecutionView/RoutinePrintView.vue'
      ),
    beforeEnter: validateRoute(requireRoutinesAuth),
    meta: {
      layout: layouts?.blank,
    },
  },
  {
    name: 'startRoutine',
    path: '/routine-definitions/:routineId/start',
    component: () =>
      import(
        /* webpackChunkName: "RoutineDefinitionView" */ '@/containers/RoutineDefinitionView/RoutineDefinitionView.vue'
      ),
    beforeEnter: validateRoute(requireRoutinesAuth),
    meta: {
      titleKey: 'routines.start_routine',
    },
  },
  // This variant is used by Rever Admins to load info from a specific organization
  {
    name: 'reverAdminEditRoutine',
    path: '/routine-definitions/:organizationId/:routineDefinitionId/edit',
    component: () =>
      import(
        /* webpackChunkName: "RoutineDefinitionFormView" */ '@/containers/RoutineDefinitionFormView/RoutineDefinitionFormView.vue'
      ),
    beforeEnter: validateRoute(requireRoutinesAuth),
    meta: {
      titleKey: 'routines.edit_routine_title',
    },
  },
  {
    name: 'editRoutine',
    path: '/routine-definitions/:routineDefinitionId/edit',
    component: () =>
      import(
        /* webpackChunkName: "RoutineDefinitionFormView" */ '@/containers/RoutineDefinitionFormView/RoutineDefinitionFormView.vue'
      ),
    beforeEnter: validateRoute(requireRoutinesAuth),
    meta: {
      titleKey: 'routines.edit_routine_title',
    },
  },
  {
    name: 'routineExecutionDetail',
    path: '/routine-executions/:routineId',
    component: () =>
      import(
        /* webpackChunkName: "RoutineExecutionView" */ '@/containers/RoutineExecutionView/RoutineExecutionView.vue'
      ),
    beforeEnter: validateRoute(requireRoutinesAuth),
    meta: {
      titleKey: 'routines.routine_one',
    },
  },
  {
    name: 'routineExecutionDetailFinding',
    path: '/routine-executions/:routineId/:findingId',
    component: () =>
      import(
        /* webpackChunkName: "RoutineExecutionView" */ '@/containers/RoutineExecutionView/RoutineExecutionView.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    meta: {
      titleKey: 'routines.finding_one',
    },
  },
  {
    name: 'directory',
    path: '/directory',
    component: () =>
      import(
        /* webpackChunkName: "UsersDirectoryView" */ '@/containers/UsersDirectoryView/UsersDirectoryView.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    meta: {
      titleKey: 'common.directory',
    },
  },
  {
    name: 'recognitions',
    path: '/recognitions',
    component: () =>
      import(
        /* webpackChunkName: "RecognitionsView" */ '@/containers/Recognitions/RecognitionsView.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    meta: {
      titleKey: 'recognitions.recognition_other',
    },
  },
  {
    name: 'organizationStructure',
    path: '/organization-structure/:organizationId',
    component: () =>
      import(
        /* webpackChunkName: "OrganizationStructureView" */ '@/containers/OrganizationStructureView/OrganizationStructureView.vue'
      ),
    beforeEnter: validateRoute(requireOrgAdminAuth),
    meta: {
      titleKey: 'common.organization_structure',
    },
  },
  {
    name: 'validateRev',
    path: '/validate/:revType/:revId',
    component: () =>
      import(
        /* webpackChunkName: "ValidationView" */ '@/containers/ValidationView/ValidationView.vue'
      ),
    beforeEnter: validateRoute(requireFinancialCoachAuth),
    meta: {
      titleKey: 'rev.validation.impact_validation',
    },
  },

  {
    name: 'tasks',
    path: '/tasks',
    component: () =>
      import(
        /* webpackChunkName: "TasksView" */ '@/containers/TasksView/TasksView.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    meta: {
      titleKey: 'common.task_other',
    },
  },
  {
    name: 'aiChatView',
    path: '/rever-ai-chat',
    component: () =>
      import(
        /* webpackChunkName: "AIChatView" */ '@/containers/AIChatView/AIChatView.vue'
      ),
    beforeEnter: validateRoute(requireAuth),
    meta: {
      titleKey: 'AI Chat',
    },
  },
  {
    name: 'aiSettings',
    path: '/ai-settings',
    component: () =>
      import(
        /* webpackChunkName: "AISettingsView" */ '@/containers/AISettings/AISettingsView.vue'
      ),

    // TODO: Implement requireOrgAIManagerAuth user special permissions.

    // IMPORTANT: ONLY ORGANIZATIONS THAT HAVE THE PLAN 'business' OR 'enterprise' can access AI Settings.
    // Check function aclsAllowedByPlan in access.js
    beforeEnter: validateRoute(requireOrgAIManagerAuth),
    meta: {
      isProtected: true,
      acl: 'ai-settings',
      titleKey: 'organization_settings.ai_settings',
    },
  },
  {
    path: '*',
    component: () =>
      import(/* webpackChunkName: "404View" */ '@/components/404View.vue'),
  },
]

function validateCompleteInformation() {
  return (to, from, next) => {
    if (hasCompleteLocation()) {
      return next({
        path: '/home',
        query: { redirect: to.fullPath },
      })
    }

    return requireAuth(to, from, next)
  }
}

function userInfoWasLoaded() {
  const { token, account } = store.state.access
  return Boolean(token && account?._id)
}

function hasCompleteLocation() {
  const access = store.state.access
  const site = access?.account?.site ?? null

  return Boolean(site)
}

function hasAcceptedTOCs() {
  return Boolean(store?.state?.access?.account?.tocsHasAccepted)
}

function validateRoute(callback) {
  return (to, from, next) => {
    if (!access.isLoggedIn()) {
      const query = to.fullPath.includes('logout')
        ? null
        : { redirect: to.fullPath }
      return next({ path: '/access', query })
    }

    // There're scenarios when user info can't be loaded due to API unavailability, race conditions or network issues (E.g. our VPN gets disconnected). In these cases, users are redirected to the "login" page on production or to the "Network error" page for development environments
    if (!userInfoWasLoaded()) {
      return next({
        path: config.env === 'production' ? '/logout' : '/network-error',
        query: { redirect: to.fullPath },
      })
    }

    if (!hasCompleteLocation()) {
      return next({
        path: '/complete-information',
        query: { redirect: to.fullPath },
      })
    }

    if (!hasAcceptedTOCs()) {
      return next({
        path: '/accept-tocs',
        query: { redirect: to.fullPath },
      })
    }

    return callback(to, from, next)
  }
}

function requireAuth(to, _from, next) {
  if (!access.isLoggedIn()) {
    const query = to.fullPath.includes('logout')
      ? null
      : { redirect: to.fullPath }
    return next({ path: '/access', query })
  }

  if (to.path === '/complete-information' && hasCompleteLocation()) {
    return next({
      path: '/home',
      query: { redirect: to.fullPath },
    })
  }

  return next()
}

function requireNotAuth(to, from, next) {
  if (!access.isLoggedIn()) {
    return next()
  }

  return next({ path: '/' })
}

function requireRoutinesAuth(to, from, next) {
  if (!access.isLoggedIn()) {
    return next({
      path: '/access',
      query: { redirect: to.fullPath },
    })
  } else {
    const account = store.getters.account
    if (!access.isFeatureEnabled(account.organization, 'routines')) {
      return next({
        path: '/404',
        query: { redirect: to.fullPath },
      })
    }
    return next()
  }
}

function requireOwnerAuth(to, from, next) {
  if (!access.isLoggedIn()) {
    return next({
      path: '/404',
      query: { redirect: to.fullPath },
    })
  } else {
    let account = store.getters.account
    if (!access.isOrganizationAdmin(account)) {
      return next({
        path: '/404',
        query: { redirect: to.fullPath },
      })
    } else {
      return next()
    }
  }
}

function requireLeadershipOrCoach(to, from, next) {
  const account = store.getters.account
  const isLeaderOrCoach = access.isLeadershipOrCoach(account)

  if (!access.isLoggedIn() || !isLeaderOrCoach) {
    return next({
      path: '/404',
      query: { redirect: to.fullPath },
    })
  }

  return next()
}

function requireFinancialCoachAuth(to, from, next) {
  const account = store.getters.account
  const isLeaderOrCoach = access.isLeadershipOrFinancialCoach(account)

  if (!access.isLoggedIn() || !isLeaderOrCoach) {
    return next({
      path: '/404',
      query: { redirect: to.fullPath },
    })
  }

  return next()
}

function requireAdminAuth(to, from, next) {
  if (!access.isLoggedIn()) {
    return next({
      path: '/404',
      query: { redirect: to.fullPath },
    })
  } else {
    let account = store.getters.account
    if (!access.isAdmin(account)) {
      return next({
        path: '/404',
        query: { redirect: to.fullPath },
      })
    }

    return next()
  }
}

function requireOrgAdminAuth(to, from, next) {
  if (!access.isLoggedIn()) {
    return next({
      path: '/404',
      query: { redirect: to.fullPath },
    })
  } else {
    const account = store.getters.account

    if (!access.isAdmin(account) && !access.isOrganizationAdmin(account)) {
      return next({
        path: '/404',
        query: { redirect: to.fullPath },
      })
    }

    return next()
  }
}
// TODO: Implement this function to check permissions on AI settings route.
function requireOrgAIManagerAuth(to, from, next) {
  if (!access.isLoggedIn()) {
    return next({
      path: '/404',
      query: { redirect: to.fullPath },
    })
  }

  const account = store.getters.account

  if (!access.isOrganizationAIManager(account)) {
    return next({
      path: '/404',
      query: { redirect: to.fullPath },
    })
  }

  return next()
}

export default routes
